<template>
  <div class="resolver-container">
    <!-- 背景装饰 -->
    <div class="leftSider">
      <img src="@assets/leftSider.png" alt="">
    </div>
    <div class="rightSider">
      <img src="@assets/rightSider.png" alt="">
    </div>

    <!-- Header -->
    <dp-header :title="'DNS解析器演化追踪'"></dp-header>

    <div class="page-update-time">
      观测周期：{{ periodText }}
      <span class="dot">·</span> 覆盖国家/地区 <span class="highlight">{{ formatNumber(countriesCovered) }}</span>
      <span class="dot">·</span> 最近扫描 <span class="highlight">{{ lastScan }}</span>
    </div>

    <!-- 机构解析链检索：基本态势顶部入口，查询后跳转到机构解析链风险查询子页面 -->
    <div class="org-entry-panel" v-show="activeTab !== 'org'">
      <div class="org-entry-head">
        <span class="org-entry-label">机构解析链检索</span>
        <el-input
          v-model="orgEntryInput"
          size="small"
          clearable
          class="org-entry-input"
          placeholder="输入机构名称（如 清华大学 / CERNET）或 AS 号（如 AS4134）后回车"
          @keyup.enter.native="submitOrgEntry"
        >
          <el-button slot="append" icon="el-icon-search" @click="submitOrgEntry" />
        </el-input>
      </div>
      <div class="org-entry-samples">
        <span class="sample-label">参考搜索：</span>
        <button
          type="button"
          class="sample-chip"
          v-for="item in orgSampleQueries"
          :key="item"
          @click="applyOrgSample(item)"
        >{{ item }}</button>
      </div>
    </div>

    <!-- 态势切换 -->
    <div class="range-switch" v-show="activeTab !== 'org'">
      <el-button
        v-for="item in tabOptions"
        :key="item.value"
        size="small"
        :type="activeTab === item.value ? 'primary' : ''"
        @click="switchTab(item.value)"
      >{{ item.label }}</el-button>
    </div>

    <div class="main-content">
      <!-- ==================== 基本态势 ==================== -->
      <div class="tab-content" v-show="activeTab === 'basic'">
        <div class="grid-row row-basic">
          <!-- 左列：概览 + Top5 分布 -->
          <div class="col-stack">
            <div class="section-card">
              <div class="card-title">
                <span class="title-main">解析器基本情况</span>
                <span class="subtitle">扫描轮数 / 累计数量 / 覆盖组织 / 最近转发器数量</span>
              </div>
              <div class="metric-grid" v-loading="overviewLoading">
                <div class="metric-box" v-for="(item, index) in kpis" :key="index">
                  <div class="metric-value">{{ formatNumber(item.value) }}</div>
                  <div class="metric-label">{{ item.label }}</div>
                </div>
              </div>
            </div>

            <div class="section-card">
              <div class="card-title">
                <span class="title-main">Top 5 国家数量分布</span>
                <span class="subtitle">累计解析器</span>
              </div>
              <div class="bar-list" v-loading="countryBarLoading">
                <div class="bar-row" v-for="(row, index) in countryRows" :key="index" :title="row.tip">
                  <div class="bar-name">{{ row.label }}</div>
                  <div class="bar-track">
                    <div class="bar-fill tone-blue" :style="{ width: row.width }"></div>
                  </div>
                  <div class="bar-count">
                    <strong>{{ row.countText }}</strong>
                    <small>{{ row.shareText }}</small>
                  </div>
                </div>
                <div class="empty-tip" v-if="!countryBarLoading && !countryRows.length">暂无数据</div>
              </div>
            </div>

            <div class="section-card">
              <div class="card-title">
                <span class="title-main">Top 5 ASN 数量分布</span>
                <span class="subtitle">累计解析器</span>
              </div>
              <div class="bar-list" v-loading="asnBarLoading">
                <div class="bar-row" v-for="(row, index) in asnRows" :key="index" :title="row.tip">
                  <div class="bar-name">{{ row.label }}</div>
                  <div class="bar-track">
                    <div class="bar-fill tone-amber" :style="{ width: row.width }"></div>
                  </div>
                  <div class="bar-count">
                    <strong>{{ row.countText }}</strong>
                    <small>{{ row.shareText }}</small>
                  </div>
                </div>
                <div class="empty-tip" v-if="!asnBarLoading && !asnRows.length">暂无数据</div>
              </div>
            </div>
          </div>

          <!-- 中列：趋势 + 换血率 -->
          <div class="col-stack">
            <div class="section-card chart-card">
              <div class="card-title">
                <span class="title-main">历史扫描解析器数量变化</span>
                <span class="subtitle">{{ trendNote }}</span>
              </div>
              <div class="chart-container" ref="trendContainer" v-loading="trendLoading"></div>
            </div>
            <div class="section-card chart-card">
              <div class="card-title">
                <span class="title-main">IP 与 /24 网络换血率波动</span>
                <span class="subtitle">相邻扫描轮次</span>
              </div>
              <div class="chart-container" ref="churnContainer" v-loading="churnLoading"></div>
            </div>
          </div>

          <!-- 右列：稳定分类 + 异动时间线 -->
          <div class="col-stack">
            <!-- IP 与 /24 稳定分类共享同一板块，避免两份相同结构纵向占满右列 -->
            <div class="section-card stability-card">
              <div class="card-title">
                <span class="title-main">{{ stabilityTitle }}</span>
                <span class="subtitle">稳定程度由高到低</span>
                <div class="tab-switch">
                  <button
                    v-for="tab in stabilityTabs"
                    :key="tab.value"
                    type="button"
                    :class="{ active: stabilityTab === tab.value }"
                    @click="stabilityTab = tab.value"
                  >{{ tab.label }}</button>
                </div>
              </div>
              <div class="stability-wrap" v-loading="stabilityLoading">
                <div class="stability-row" v-for="(row, index) in stabilityRows" :key="index">
                  <div class="stab-info">
                    <div class="stab-name">{{ row.name }}</div>
                    <span class="stab-range">{{ row.range }} · {{ row.desc }}</span>
                  </div>
                  <div class="bar-track">
                    <div class="bar-fill" :class="'tone-' + row.tone" :style="{ width: row.width }"></div>
                  </div>
                  <div class="stab-value">
                    {{ row.countText }}
                    <small>{{ row.shareText }}</small>
                  </div>
                </div>
                <div class="empty-tip" v-if="!stabilityLoading && !stabilityRows.length">暂无数据</div>
              </div>
            </div>

            <div class="section-card chart-card">
              <div class="card-title">
                <span class="title-main">{{ timelineTab === 'country' ? '国家级异动时间线' : 'ASN 级异常时间线' }}</span>
                <span class="subtitle">{{ timelineNote }}</span>
                <div class="tab-switch">
                  <button
                    v-for="tab in timelineTabs"
                    :key="tab.value"
                    type="button"
                    :class="{ active: timelineTab === tab.value }"
                    @click="switchTimelineTab(tab.value)"
                  >{{ tab.label }}</button>
                </div>
              </div>
              <div class="chart-container" ref="timelineContainer" v-loading="timelineLoading"></div>
            </div>
          </div>
        </div>

        <!-- 异动事件查询 -->
        <div class="section-card">
          <div class="card-title">
            <span class="title-main">异动事件查询</span>
            <span class="subtitle">{{ eventNote }}</span>
          </div>

          <div class="query-toolbar">
            <el-input
              v-model="eventSearch"
              size="small"
              clearable
              placeholder="搜索国家（如 US） / AS 号（如 4538） / 组织 / 备注"
              @input="onEventFilterChange"
              @clear="onEventFilterChange"
            ></el-input>
            <el-select v-model="eventDirection" size="small" placeholder="全部方向" clearable @change="onEventFilterChange">
              <el-option v-for="item in directionOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-select v-model="eventYear" size="small" placeholder="全部时间" clearable @change="onEventFilterChange">
              <el-option v-for="item in yearOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-button size="small" @click="resetEventFilter">重置</el-button>
          </div>

          <!-- 查询画像：仅在命中国家 / AS 时展示 -->
          <div class="query-insight" v-show="profileVisible">
            <div class="query-cell">
              <div class="query-cell-head">
                <h3>{{ profileTitle }}</h3>
                <span>{{ profileNote }}</span>
              </div>
              <div class="chart-container profile-chart" ref="profileContainer" v-loading="profileLoading"></div>
              <div class="trajectory-grid">
                <div class="trajectory-item" v-for="(item, index) in trajectoryItems" :key="index">
                  <div class="traj-name">{{ item.name }}</div>
                  <div class="traj-value">{{ item.value }}</div>
                  <div class="traj-meta">{{ item.meta }}</div>
                </div>
              </div>
              <div class="trajectory-note">
                <span v-for="(item, index) in changeNotes" :key="index">
                  {{ item.label }}: <b :class="item.tone">{{ item.text }}</b>
                </span>
              </div>
            </div>
            <div class="query-cell">
              <div class="query-cell-head">
                <h3>{{ breakdownTitle }}</h3>
                <span>{{ breakdownNote }}</span>
              </div>
              <div class="bar-list">
                <div class="bar-row" v-for="(row, index) in breakdownRows" :key="index" :title="row.tip">
                  <div class="bar-name">{{ row.label }}</div>
                  <div class="bar-track">
                    <div class="bar-fill" :class="row.tone" :style="{ width: row.width }"></div>
                  </div>
                  <div class="bar-count">
                    <strong>{{ row.countText }}</strong>
                    <small>{{ row.shareText }}</small>
                  </div>
                </div>
                <div class="empty-tip" v-if="!profileLoading && !breakdownRows.length">暂无组成分布</div>
              </div>
            </div>
          </div>

          <el-table
            ref="eventTable"
            :data="eventPageRows"
            v-loading="eventsLoading"
            style="width: 100%"
            class="dark-table event-table"
          >
            <el-table-column prop="scan" label="时间" min-width="100"></el-table-column>
            <el-table-column prop="typeLabel" label="类型" min-width="80"></el-table-column>
            <el-table-column prop="entity" label="对象" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column label="方向" min-width="90" align="center">
              <template slot-scope="scope">
                <span class="direction-tag" :class="scope.row.toneClass">{{ scope.row.direction }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="changeText" label="变化幅度" min-width="150"></el-table-column>
            <el-table-column prop="note" label="备注" min-width="300" show-overflow-tooltip></el-table-column>
          </el-table>
          <el-pagination
            v-if="eventTotal > eventPageSize"
            layout="total, prev, pager, next"
            :current-page="eventPage"
            :page-size="eventPageSize"
            :total="eventTotal"
            @current-change="handleEventPageChange"
            class="dark-pagination"
          />
        </div>
      </div>

      <!-- ==================== 风险态势 ==================== -->
      <div class="tab-content" v-show="activeTab === 'risk'">
        <!-- 两张地图占据完整的第一行；下方统计按信息密度采用 2:1 布局 -->
        <div class="risk-overview-grid">
          <div class="section-card chart-card map-card risk-single-map-card">
            <div class="card-title">
              <span class="title-main">集中单点依赖地图</span>
              <span class="subtitle">{{ singleMapNote }}</span>
            </div>
            <div class="chart-container map-chart" ref="singleMapContainer" v-loading="singleMapLoading"></div>
          </div>

          <div class="section-card chart-card map-card risk-egress-map-card">
            <div class="card-title">
              <span class="title-main">中国解析器解析流量出境地图</span>
              <span class="subtitle">{{ egressNote }}</span>
            </div>
            <div class="chart-container map-chart" ref="egressMapContainer" v-loading="egressLoading"></div>
          </div>

          <div class="section-card chart-card risk-impact-card">
            <div class="card-title">
              <span class="title-main">全量单点上游影响规模分布</span>
              <span class="subtitle">{{ impactNote }}</span>
            </div>
            <div class="chart-container compact-chart" ref="impactContainer" v-loading="impactLoading"></div>
          </div>

          <div class="section-card chart-card risk-jurisdiction-card">
            <div class="card-title">
              <span class="title-main">国内下游 DNS 解析器与上游解析器对比分析</span>
              <span class="subtitle">{{ jurisdictionNote }}</span>
            </div>
            <div class="chart-container compact-chart" ref="jurisdictionContainer" v-loading="jurisdictionLoading"></div>
          </div>
        </div>

        <!-- 软件 / CVE 过滤 -->
        <div class="section-card filter-card">
          <div class="risk-filter">
            <span class="filter-label">软件/CVE过滤</span>
            <el-select v-model="riskFilterType" size="small" placeholder="全部维度" clearable @change="onRiskFilterTypeChange">
              <el-option label="国家/地区" value="country"></el-option>
              <el-option label="机构" value="org"></el-option>
              <el-option label="AS" value="asn"></el-option>
            </el-select>
            <el-select
              v-model="riskFilterValue"
              size="small"
              filterable
              clearable
              :disabled="!riskFilterValueOptions.length"
              placeholder="全部对象"
              @change="renderSoftwareCharts"
            >
              <el-option
                v-for="item in riskFilterValueOptions"
                :key="item.key"
                :label="item.label + ' · ' + formatNumber(item.count)"
                :value="item.key"
              ></el-option>
            </el-select>
            <el-button size="small" @click="resetRiskFilter">重置</el-button>
            <span class="filter-scope">软件/CVE样本: <b>{{ riskScopeLabel }}</b></span>
          </div>
        </div>

        <div class="grid-row row-software">
          <div class="section-card chart-card">
            <div class="card-title">
              <span class="title-main">1. 软件类型</span>
              <span class="subtitle">IP 占比</span>
            </div>
            <div class="chart-container" ref="softwareTypeContainer" v-loading="softwareLoading"></div>
          </div>
          <div class="section-card chart-card">
            <div class="card-title">
              <span class="title-main">2. 软件受影响</span>
              <span class="subtitle">IP 数</span>
            </div>
            <div class="chart-container" ref="softwareImpactContainer" v-loading="softwareLoading"></div>
          </div>
          <div class="section-card chart-card">
            <div class="card-title">
              <span class="title-main">3. CVE Top</span>
              <span class="subtitle">影响 IP</span>
            </div>
            <div class="chart-container" ref="cveTopContainer" v-loading="softwareLoading"></div>
          </div>
          <div class="section-card chart-card">
            <div class="card-title">
              <span class="title-main">4. 软件 × CVE</span>
              <span class="subtitle">热力</span>
            </div>
            <div class="chart-container" ref="matrixContainer" v-loading="softwareLoading"></div>
          </div>
          <div class="section-card chart-card">
            <div class="card-title">
              <span class="title-main">5. 风险等级</span>
              <span class="subtitle">影响条目</span>
            </div>
            <div class="chart-container" ref="levelContainer" v-loading="softwareLoading"></div>
          </div>
          <div class="section-card chart-card">
            <div class="card-title">
              <span class="title-main">6. 老版本占比</span>
              <span class="subtitle">优先级</span>
            </div>
            <div class="chart-container legacy-chart" ref="legacyContainer" v-loading="softwareLoading"></div>
            <div class="pill-row">
              <span class="risk-pill" v-for="(item, index) in legacyVersions" :key="index">
                {{ item.version }} · {{ item.shareText }}
              </span>
            </div>
          </div>
        </div>

        <!-- 风险查询 -->
        <div class="section-card">
          <div class="card-title">
            <span class="title-main">风险查询</span>
            <span class="subtitle">{{ riskQueryNote }}</span>
          </div>

          <div class="query-toolbar risk-query-toolbar">
            <el-input v-model="rqOrg" size="small" clearable placeholder="机构名" @input="onRiskQueryFilterChange" @clear="onRiskQueryFilterChange"></el-input>
            <el-select v-model="rqType" size="small" clearable placeholder="全部风险类型" @change="onRiskQueryFilterChange">
              <el-option
                v-for="item in riskTypeOptions"
                :key="item.key"
                :label="item.label + ' · ' + formatNumber(item.count)"
                :value="item.key"
              ></el-option>
            </el-select>
            <el-input v-model="rqCountry" size="small" clearable placeholder="国家/地区" @input="onRiskQueryFilterChange" @clear="onRiskQueryFilterChange"></el-input>
            <el-input v-model="rqAsn" size="small" clearable placeholder="AS号" @input="onRiskQueryFilterChange" @clear="onRiskQueryFilterChange"></el-input>
            <el-button size="small" @click="resetRiskQueryFilter">重置</el-button>
          </div>

          <el-table
            :data="riskQueryPageRows"
            v-loading="riskQueryLoading"
            style="width: 100%"
            class="dark-table risk-query-table"
          >
            <el-table-column prop="detect_date" label="检测日期" min-width="105"></el-table-column>
            <el-table-column prop="resolver" label="解析器" min-width="135">
              <template slot-scope="scope">
                <span class="resolver-cell">{{ scope.row.resolver }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="role" label="角色" min-width="100"></el-table-column>
            <el-table-column label="结构风险类型" min-width="170">
              <template slot-scope="scope">
                <div class="risk-tag-list">
                  <span
                    v-for="(tag, index) in scope.row.riskTags"
                    :key="index"
                    class="risk-tag"
                    :class="{ neutral: tag === '未见明显结构风险' }"
                  >{{ tag }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="存在CVE" min-width="220" show-overflow-tooltip>
              <template slot-scope="scope">
                <span class="cve-cell" :class="{ hit: scope.row.cveHit }">{{ scope.row.cveText }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="org" label="所属机构" min-width="160" show-overflow-tooltip></el-table-column>
            <el-table-column prop="countryLabel" label="所属国家" min-width="140" show-overflow-tooltip></el-table-column>
            <el-table-column prop="asnLabel" label="所属AS" min-width="100"></el-table-column>
          </el-table>
          <el-pagination
            v-if="riskQueryTotal > riskQueryPageSize"
            layout="total, prev, pager, next"
            :current-page="riskQueryPage"
            :page-size="riskQueryPageSize"
            :total="riskQueryTotal"
            @current-change="handleRiskQueryPageChange"
            class="dark-pagination"
          />
        </div>
      </div>

      <!-- ==================== 机构解析链风险查询 ==================== -->
      <div class="tab-content" v-show="activeTab === 'org'">
        <div class="org-back-bar">
          <el-button size="small" type="warning" icon="el-icon-back" @click="backToOverview">返回总览</el-button>
          <span class="org-back-note">{{ orgBackNote }}</span>
        </div>
        <org-dependency
          v-if="orgPanelMounted"
          ref="orgPanel"
          :active="activeTab === 'org'"
          :initial-query="orgPendingQuery"
          :initial-asn="orgPendingAsn"
          @select="handleOrgSelect"
        ></org-dependency>
      </div>

      <footer class="beian-footer">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">鲁ICP备2026032922号-1</a>
        <span>|</span>
        <a
          class="beian-police-link"
          href="https://beian.mps.gov.cn/#/query/webSearch?code=37140202001764"
          rel="noreferrer"
          target="_blank"
        >
          <img class="beian-police-logo" src="/beian-police.png" alt="公安备案图标" />
          <span>鲁公网安备37140202001764号</span>
        </a>
      </footer>
    </div>

    <!-- 底部导航 -->
    <navigation ref="nav"></navigation>
  </div>
</template>

<script>
import * as echarts from "echarts";
// 注意：全局已注册名为 worldMapData 的组件，这里的底图 GeoJSON 另起变量名以免混淆
import worldGeoJson from "@/components/world.json";
import OrgDependency from "@/components/org-dependency.vue";

import {
  getResolverOverview,
  getResolverTrend,
  getResolverTopCountries,
  getResolverTopAsns,
  getResolverChurn,
  getResolverIpStability,
  getResolverNet24Stability,
  getResolverCountryTimeline,
  getResolverAsnTimeline,
  getResolverAnomalyEvents,
  getResolverCountryProfiles,
  getResolverAsnProfiles,
  getResolverRiskSingleFailureMap,
  getResolverRiskImpactDistribution,
  getResolverRiskChinaEgressMap,
  getResolverRiskJurisdiction,
  getResolverRiskSoftwareType,
  getResolverRiskSoftwareImpact,
  getResolverRiskCveTop,
  getResolverRiskSoftwareCveMatrix,
  getResolverRiskLevelDistribution,
  getResolverRiskLegacyVersionShare,
  getResolverRiskQueryResults
} from "@/service/dashboard";

// 上游地图数据把经纬度预投影成了 1000x520 画布坐标（等距圆柱投影）：
//   x = 40 + (lon + 180) / 360 * 920 ; y = 40 + (90 - lat) / 180 * 420
// echarts geo 需要的是经纬度，这里做一次逆变换复用现成的 world.json 底图
const MAP_X0 = 40;
const MAP_WIDTH = 920;
const MAP_Y0 = 40;
const MAP_HEIGHT = 420;

const toLon = (x) => ((Number(x) || 0) - MAP_X0) / MAP_WIDTH * 360 - 180;
const toLat = (y) => 90 - ((Number(y) || 0) - MAP_Y0) / MAP_HEIGHT * 180;

// 风险态势以蓝色为主数据色，只在“增减 / 风险等级”等有语义的状态上使用暖色，避免彩虹式配色
const DATA_BLUE = "#3987E5";
const DATA_BLUE_LIGHT = "#86B6EF";
const DATA_BLUE_DARK = "#184F95";
const DATA_SLATE = "#8C9BB5";
const RISK_RED = "#E66767";
const RISK_ORANGE = "#EC835A";
const RISK_AMBER = "#FAB219";
const PALETTE = [DATA_BLUE, "#D95926", "#199E70"];
const AXIS_TEXT = "rgba(255,255,255,0.6)";
const AXIS_LINE = "rgba(255,255,255,0.2)";
const SPLIT_LINE = "rgba(255,255,255,0.08)";

// 事件表 / 风险查询表均为前端分页，单页条数
const PAGE_SIZE = 20;

// 机构解析链检索状态（关键词 + 选中 ASN）在刷新间的保存位置
const ORG_STATE_KEY = "resolverEvolution.orgSearch";

export default {
  name: "ResolverEvolution",
  components: { OrgDependency },
  data() {
    return {
      activeTab: this.resolveInitialTab(),
      orgPanelMounted: false,
      tabOptions: [
        { label: "基本态势", value: "basic" },
        { label: "风险态势", value: "risk" }
      ],

      // 基本态势顶部的机构解析链检索入口
      orgEntryInput: "",
      // 与 org-dependency 内部搜索框的示例保持一致，降低使用门槛
      orgSampleQueries: ["清华大学", "CERNET", "China Telecom", "AS4134", "AS4538", "AS4812"],
      // 顶部输入的关键词，org-dependency 挂载/更新后自动执行一次查询
      orgPendingQuery: "",
      // 上次选中的机构 ASN，刷新后连同检索词一起恢复，直接回到该机构的解析链
      orgPendingAsn: "",
      orgBackNote: "返回总览后可继续查看其余态势面板",

      // 概览
      overviewLoading: false,
      periodText: "加载中",
      lastScan: "--",
      countriesCovered: 0,
      kpis: [],

      // 趋势 / 换血率
      trendLoading: false,
      trendNote: "--",
      churnLoading: false,

      // Top5 分布
      countryBarLoading: false,
      countryRows: [],
      asnBarLoading: false,
      asnRows: [],

      // 稳定分类（IP / /24 在同一板块内切换）
      stabilityTab: "ip",
      stabilityTabs: [
        { label: "IP", value: "ip" },
        { label: "/24 网段", value: "net24" }
      ],
      ipStabilityLoading: false,
      ipStabilityRows: [],
      net24StabilityLoading: false,
      net24StabilityRows: [],

      // 异动时间线
      timelineLoading: false,
      timelineTab: "country",
      timelineTabs: [
        { label: "国家级", value: "country" },
        { label: "ASN级", value: "asn" }
      ],
      countryTimelineNote: "--",
      asnTimelineNote: "--",

      // 异动事件查询
      eventsLoading: false,
      eventSearch: "",
      eventDirection: "",
      eventYear: "",
      directionOptions: ["新增", "激增", "骤降", "清零"],
      yearOptions: [],
      eventNote: "加载中",
      eventPageRows: [],
      eventTotal: 0,
      eventPage: 1,
      eventPageSize: PAGE_SIZE,

      // 查询画像
      profileVisible: false,
      profileLoading: false,
      profileTitle: "历史解析器数量趋势",
      profileNote: "--",
      trajectoryItems: [],
      changeNotes: [],
      breakdownTitle: "Top 10 组成分布",
      breakdownNote: "按最新轮数量排序",
      breakdownRows: [],

      // 风险态势
      singleMapLoading: false,
      singleMapNote: "加载中",
      impactLoading: false,
      impactNote: "加载中",
      egressLoading: false,
      egressNote: "加载中",
      jurisdictionLoading: false,
      jurisdictionNote: "加载中",

      // 软件 / CVE
      softwareLoading: false,
      riskFilterType: "",
      riskFilterValue: "",
      riskFilterSource: { countries: [], asns: [], orgs: [] },
      riskScopeLabel: "全部软件/CVE样本",
      legacyVersions: [],

      // 风险查询
      riskQueryLoading: false,
      riskQueryNote: "等待加载",
      riskTypeOptions: [],
      rqOrg: "",
      rqType: "",
      rqCountry: "",
      rqAsn: "",
      riskQueryPageRows: [],
      riskQueryTotal: 0,
      riskQueryPage: 1,
      riskQueryPageSize: PAGE_SIZE,

      resizeTimer: null
    };
  },
  computed: {
    stabilityTitle() {
      return this.stabilityTab === "ip" ? "IP 稳定分类" : "/24 网络稳定分类";
    },
    stabilityLoading() {
      return this.stabilityTab === "ip" ? this.ipStabilityLoading : this.net24StabilityLoading;
    },
    stabilityRows() {
      return this.stabilityTab === "ip" ? this.ipStabilityRows : this.net24StabilityRows;
    },
    timelineNote() {
      return this.timelineTab === "country" ? this.countryTimelineNote : this.asnTimelineNote;
    },
    riskFilterValueOptions() {
      const key = this.riskFilterType === "country"
        ? "countries"
        : this.riskFilterType === "asn"
          ? "asns"
          : this.riskFilterType === "org"
            ? "orgs"
            : "";

      return key ? (this.riskFilterSource[key] || []) : [];
    }
  },
  created() {
    // 大体量数据（异动事件 1 万条、风险查询 12 万条、ASN 画像 23MB）不放进 data，
    // 避免 Vue 2 深度响应式遍历带来的内存与耗时开销
    this.charts = {};
    this.rawEventRows = [];
    this.eventFilteredRows = [];
    this.timelineRows = { country: [], asn: [] };
    this.countryProfiles = null;
    this.asnProfiles = null;
    this.riskSoftwareData = {};
    this.riskQueryColumnIndex = {};
    this.rawRiskQueryRows = [];
    this.riskQueryFilteredRows = [];
    this.queryToken = 0;
    this.eventFilterTimer = null;
    this.riskQueryFilterTimer = null;
    this.riskLoaded = false;
    // 基本态势面板是否已加载过；仅影响是否重复拉取，不影响模块自身 loading 态
    this.basicLoaded = false;
    this.riskLoading = null;
    this.riskChartsInited = false;
    this.profileChartInited = false;
    this.visibilityResizeTimer = null;
  },
  mounted() {
    echarts.registerMap("world", worldGeoJson);
    if (this.activeTab === "org") {
      this.orgPanelMounted = true;
    }
    // 刷新时恢复上次的机构检索：URL 里带 ?tab=org 只是回到页签，
    // 检索词和选中机构存在 sessionStorage，回填后由子组件自动重跑一次查询
    this.restoreOrgState();
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.activeTab === "risk") {
          this.initRiskCharts();
          this.loadRiskPanels();
          this.resizeVisibleCharts();
        } else if (this.activeTab === "basic") {
          this.initBasicCharts();
          this.loadBasicPanels();
        }
      }, 50);
    });
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].dispose();
      }
    });
    this.charts = {};
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.eventFilterTimer) {
      clearTimeout(this.eventFilterTimer);
    }
    if (this.riskQueryFilterTimer) {
      clearTimeout(this.riskQueryFilterTimer);
    }
    if (this.visibilityResizeTimer) {
      clearTimeout(this.visibilityResizeTimer);
    }
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    // 导航栏“基本态势 / 风险态势”子项点击后通过 query 切换
    "$route.query.tab"(tab) {
      const target = tab === "risk" || tab === "org" ? tab : "basic";

      if (this.activeTab !== target) {
        this.switchTab(target);
      }
    }
  },
  methods: {
    // 导航栏子项带 ?tab= 深链；机构解析链查询复用同一路由，用 tab=org 区分
    resolveInitialTab() {
      const tab = (this.$route && this.$route.query && this.$route.query.tab) || "";

      if (tab === "risk" || tab === "org") {
        return tab;
      }
      return "basic";
    },

    // ==================== 通用工具 ====================
    formatNumber(value) {
      const num = Number(value);

      return Number.isFinite(num) ? num.toLocaleString("en-US") : "0";
    },

    formatCompact(value) {
      const num = Math.abs(Number(value) || 0);
      const sign = Number(value) < 0 ? "-" : "";

      if (num >= 100000000) {
        return `${sign}${(num / 100000000).toFixed(1)}亿`;
      }
      if (num >= 10000) {
        return `${sign}${(num / 10000).toFixed(1)}万`;
      }
      return `${sign}${num.toLocaleString("en-US")}`;
    },

    formatShare(value) {
      return `${(Number(value) || 0).toFixed(2)}%`;
    },

    formatPct(value) {
      const num = Number(value) || 0;

      return `${num > 0 ? "+" : ""}${num.toFixed(1)}%`;
    },

    formatSignedInt(value) {
      const num = Number(value) || 0;

      return `${num > 0 ? "+" : ""}${this.formatNumber(num)}`;
    },

    formatTrajectoryPct(value) {
      if (value === null || value === undefined || !Number.isFinite(Number(value))) {
        return "不可比";
      }
      return this.formatPct(value);
    },

    shortText(value, limit = 22) {
      const text = String(value || "");

      return text.length > limit ? `${text.slice(0, limit - 1)}...` : text;
    },

    // 上游把无法定位的归属标记为 XX，展示时统一剔除
    withoutUnknownCountry(rows) {
      return (rows || []).filter(row => String(row.country || "").toUpperCase() !== "XX");
    },

    // 把 rows 转成带宽度/占比文案的条形数据
    toBarRows(rows, labelFn, valueKey = "count", tone = "tone-blue") {
      const list = rows || [];
      const max = Math.max(1, ...list.map(row => Number(row[valueKey] || 0)));

      return list.map(row => {
        const value = Number(row[valueKey] || 0);
        const label = labelFn(row);

        return {
          label,
          tone,
          width: `${Math.max(3, value / max * 100).toFixed(1)}%`,
          countText: this.formatNumber(value),
          shareText: this.formatShare(row.share_pct),
          tip: `${label} · ${this.formatNumber(value)} · ${this.formatShare(row.share_pct)}`
        };
      });
    },

    toStabilityRows(rows) {
      const list = rows || [];
      const max = Math.max(1, ...list.map(row => Number(row.count || 0)));

      return list.map(row => ({
        name: row.name,
        range: row.range,
        desc: row.desc,
        tone: row.tone,
        width: `${Math.max(1, Number(row.count || 0) / max * 100).toFixed(1)}%`,
        countText: this.formatNumber(row.count),
        shareText: this.formatShare(row.share_pct)
      }));
    },

    // ==================== 图表基础设施 ====================
    getChart(key, refName) {
      if (this.charts[key]) {
        return this.charts[key];
      }
      const el = this.$refs[refName];

      if (!el || (!el.offsetParent && !el.clientWidth)) {
        return null;
      }
      this.charts[key] = echarts.init(el);
      return this.charts[key];
    },

    setChartOption(key, refName, option) {
      const chart = this.getChart(key, refName);

      if (chart) {
        chart.setOption(option, true);
      }
    },

    showChartEmpty(key, refName, text) {
      this.setChartOption(key, refName, {
        title: {
          text: text || "暂无数据",
          left: "center",
          top: "middle",
          textStyle: { color: "rgba(255,255,255,0.45)", fontSize: 13, fontWeight: "normal" }
        }
      });
    },

    initBasicCharts() {
      ["trendContainer", "churnContainer", "timelineContainer"].forEach(refName => {
        const key = refName.replace("Container", "");

        if (!this.charts[key] && this.$refs[refName]) {
          this.charts[key] = echarts.init(this.$refs[refName]);
        }
      });
    },

    initRiskCharts() {
      if (this.riskChartsInited) {
        return;
      }
      [
        "singleMapContainer", "egressMapContainer", "impactContainer", "jurisdictionContainer",
        "softwareTypeContainer", "softwareImpactContainer", "cveTopContainer",
        "matrixContainer", "levelContainer", "legacyContainer"
      ].forEach(refName => {
        const key = refName.replace("Container", "");

        if (!this.charts[key] && this.$refs[refName]) {
          this.charts[key] = echarts.init(this.$refs[refName]);
        }
      });
      this.riskChartsInited = true;
    },

    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => this.resizeVisibleCharts(), 100);
    },

    // 不 resize 被 v-show 隐藏的图表：隐藏容器尺寸为 0，强制 resize 会让地图再次显示时空白
    resizeVisibleCharts() {
      const basicKeys = ["trend", "churn", "timeline"];
      const riskKeys = [
        "singleMap", "egressMap", "impact", "jurisdiction",
        "softwareType", "softwareImpact", "cveTop", "matrix", "level", "legacy"
      ];
      const keys = this.activeTab === "risk" ? riskKeys : basicKeys;

      if (this.activeTab === "basic" && this.profileVisible) {
        keys.push("profile");
      }
      keys.forEach(key => {
        if (this.charts[key]) {
          this.charts[key].resize();
        }
      });
    },

    switchTab(tab) {
      // 同步 URL，便于导航栏“基本态势/风险态势”子项高亮与分享
      if (this.$route.query.tab !== tab) {
        this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, tab } });
      }
      if (this.activeTab === tab) {
        return;
      }
      this.activeTab = tab;
      // 机构面板体积大（索引 4.3MB），首次切到该页签时才挂载
      if (tab === "org") {
        this.orgPanelMounted = true;
      }
      if (tab === "basic") {
        // 从风险态势/机构解析链切回基本态势时必须补拉数据，
        // 否则整页模块停在“缺失数据”的空态，只能刷新才恢复
        this.basicLoaded || this.loadBasicPanels();
      }
      this.$nextTick(() => {
        // 等浏览器完成 v-show 布局后再初始化/缩放；第二次延迟校正用于地图多 canvas 图层
        window.requestAnimationFrame(() => {
          if (tab === "basic") {
            this.initBasicCharts();
          } else if (tab === "risk") {
            this.initRiskCharts();
            this.loadRiskPanels();
          }
          this.resizeVisibleCharts();
          if (this.visibilityResizeTimer) {
            clearTimeout(this.visibilityResizeTimer);
          }
          this.visibilityResizeTimer = setTimeout(() => this.resizeVisibleCharts(), 160);
        });
      });
    },

    // ==================== 机构解析链检索入口 ====================
    applyOrgSample(text) {
      this.orgEntryInput = text;
      this.submitOrgEntry();
    },

    // 跳转到机构解析链风险查询子页面并带上关键词，由子组件挂载后自动执行
    submitOrgEntry() {
      const query = this.orgEntryInput.trim();

      if (!query) {
        this.$message.warning("请输入机构名或 AS 号");
        return;
      }
      this.orgPendingQuery = query;
      // 新的关键词意味着要重新匹配，先清掉上次选中的机构
      this.orgPendingAsn = "";
      this.saveOrgState();
      this.switchTab("org");
    },

    // 子组件选中机构后回传 ASN，用于刷新时精确还原（同一关键词可能匹配到多个机构）
    handleOrgSelect(payload) {
      this.orgPendingAsn = (payload && payload.asn) || "";
      this.saveOrgState();
    },

    saveOrgState() {
      try {
        if (!this.orgPendingQuery) {
          window.sessionStorage.removeItem(ORG_STATE_KEY);
          return;
        }
        window.sessionStorage.setItem(ORG_STATE_KEY, JSON.stringify({
          query: this.orgPendingQuery,
          asn: this.orgPendingAsn || ""
        }));
      } catch (error) {
        // 隐私模式等场景下 sessionStorage 不可用，仅丢失刷新恢复能力，不影响检索
        console.warn("[ResolverEvolution] 保存机构检索状态失败:", error);
      }
    },

    // 刷新后回填检索框与选中机构；没有历史检索则不做任何事（保持默认总览）
    restoreOrgState() {
      let saved = null;

      try {
        saved = JSON.parse(window.sessionStorage.getItem(ORG_STATE_KEY) || "null");
      } catch (error) {
        saved = null;
      }
      if (!saved || !saved.query) {
        return;
      }
      this.orgPendingQuery = String(saved.query);
      this.orgPendingAsn = saved.asn ? String(saved.asn) : "";
      this.orgEntryInput = this.orgPendingQuery;
      // 带检索词刷新时直接落在机构解析链页签，省掉一次手动切换
      if (this.activeTab === "basic") {
        this.activeTab = "org";
      }
      this.orgPanelMounted = true;
    },

    // 返回基本态势总览，并释放检索结果（大机构明细可达上千节点，常驻会拖慢页面）
    backToOverview() {
      this.orgPendingQuery = "";
      this.orgPendingAsn = "";
      this.orgEntryInput = "";
      this.orgPanelMounted = false;
      this.saveOrgState();
      this.switchTab("basic");
    },

    // ==================== 基本态势加载 ====================
    loadBasicPanels() {
      // 切回基本态势时复用，避免重复拉取
      this.basicLoaded = true;
      this.loadOverview();
      this.loadTrend();
      this.loadChurn();
      this.loadTopCountries();
      this.loadTopAsns();
      this.loadIpStability();
      this.loadNet24Stability();
      this.loadTimelines();
      this.loadAnomalyEvents();
    },

    async loadOverview() {
      this.overviewLoading = true;
      try {
        const res = await getResolverOverview();

        this.periodText = res.period || "--";
        this.lastScan = res.last_scan || "--";
        this.countriesCovered = res.countries_covered || 0;
        this.kpis = res.kpis && res.kpis.length
          ? res.kpis
          : [
            { label: "扫描轮数", value: res.rounds },
            { label: "累计数量", value: res.cumulative_ips },
            { label: "覆盖组织", value: res.covered_orgs },
            { label: "最近转发器数量", value: res.forwarder_total }
          ];
      } catch (error) {
        console.error("[ResolverEvolution] loadOverview 失败:", error);
        this.periodText = "加载失败";
      } finally {
        this.overviewLoading = false;
      }
    },

    async loadTrend() {
      this.trendLoading = true;
      try {
        const res = await getResolverTrend();
        const values = res.values || [];

        this.trendNote = `最新 ${this.formatNumber(res.latest_ip_count)} 个`;
        this.setChartOption("trend", "trendContainer", this.buildLineOption([{
          name: "活跃解析器",
          color: "#1890FF",
          values
        }], { area: true }));
      } catch (error) {
        console.error("[ResolverEvolution] loadTrend 失败:", error);
        this.trendNote = "加载失败";
        this.showChartEmpty("trend", "trendContainer", "数据加载失败");
      } finally {
        this.trendLoading = false;
      }
    },

    async loadChurn() {
      this.churnLoading = true;
      try {
        const res = await getResolverChurn();
        const series = (res.series || []).map((item, index) => ({
          name: item.name,
          color: index === 0 ? "#FF6B6B" : "#13C2C2",
          values: item.values || []
        }));

        this.setChartOption("churn", "churnContainer", this.buildLineOption(series, { percent: true }));
      } catch (error) {
        console.error("[ResolverEvolution] loadChurn 失败:", error);
        this.showChartEmpty("churn", "churnContainer", "数据加载失败");
      } finally {
        this.churnLoading = false;
      }
    },

    // 折线图：趋势 / 换血率 / 查询画像共用
    buildLineOption(seriesList, options = {}) {
      const list = seriesList || [];
      const labels = (list[0] && list[0].values || []).map(item => item.label);
      const percent = Boolean(options.percent);
      const unitFormatter = value => (percent
        ? `${(Number(value) || 0).toFixed(1)}%`
        : this.formatNumber(value));

      return {
        color: list.map(item => item.color),
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const head = params[0] ? params[0].axisValue : "";
            const body = params
              .map(item => `${item.marker}${item.seriesName}: ${unitFormatter(item.value)}`)
              .join("<br/>");

            return `${head}<br/>${body}`;
          }
        },
        legend: {
          show: list.length > 1,
          data: list.map(item => item.name),
          top: 2,
          textStyle: { color: "rgba(255,255,255,0.75)", fontSize: 11 },
          itemWidth: 14,
          itemHeight: 8
        },
        grid: { left: 8, right: 18, top: list.length > 1 ? 34 : 16, bottom: 4, containLabel: true },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: labels,
          axisLabel: { color: AXIS_TEXT, fontSize: 10, hideOverlap: true },
          axisLine: { lineStyle: { color: AXIS_LINE } },
          axisTick: { show: false }
        },
        yAxis: {
          type: "value",
          axisLabel: {
            color: AXIS_TEXT,
            fontSize: 10,
            formatter: value => (percent ? `${value}%` : this.formatCompact(value))
          },
          splitLine: { lineStyle: { color: SPLIT_LINE } }
        },
        series: list.map(item => ({
          name: item.name,
          type: "line",
          smooth: true,
          showSymbol: false,
          symbolSize: 6,
          lineStyle: { width: 2 },
          areaStyle: options.area
            ? {
              opacity: 0.18,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: item.color },
                { offset: 1, color: "rgba(0,0,0,0)" }
              ])
            }
            : undefined,
          data: (item.values || []).map(point => Number(point.value || 0))
        }))
      };
    },

    async loadTopCountries() {
      this.countryBarLoading = true;
      try {
        const res = await getResolverTopCountries();

        this.countryRows = this.toBarRows(res.rows, row => `${row.country} · ${row.region}`, "count", "tone-blue");
      } catch (error) {
        console.error("[ResolverEvolution] loadTopCountries 失败:", error);
        this.countryRows = [];
      } finally {
        this.countryBarLoading = false;
      }
    },

    async loadTopAsns() {
      this.asnBarLoading = true;
      try {
        const res = await getResolverTopAsns();

        this.asnRows = this.toBarRows(res.rows, row => `AS${row.asn} ${this.shortText(row.org, 18)}`, "count", "tone-amber");
      } catch (error) {
        console.error("[ResolverEvolution] loadTopAsns 失败:", error);
        this.asnRows = [];
      } finally {
        this.asnBarLoading = false;
      }
    },

    async loadIpStability() {
      this.ipStabilityLoading = true;
      try {
        const res = await getResolverIpStability();

        this.ipStabilityRows = this.toStabilityRows(res.rows);
      } catch (error) {
        console.error("[ResolverEvolution] loadIpStability 失败:", error);
        this.ipStabilityRows = [];
      } finally {
        this.ipStabilityLoading = false;
      }
    },

    async loadNet24Stability() {
      this.net24StabilityLoading = true;
      try {
        const res = await getResolverNet24Stability();

        this.net24StabilityRows = this.toStabilityRows(res.rows);
      } catch (error) {
        console.error("[ResolverEvolution] loadNet24Stability 失败:", error);
        this.net24StabilityRows = [];
      } finally {
        this.net24StabilityLoading = false;
      }
    },

    async loadTimelines() {
      this.timelineLoading = true;
      try {
        const [countryRes, asnRes] = await Promise.all([
          getResolverCountryTimeline(),
          getResolverAsnTimeline()
        ]);
        const countryRows = this.withoutUnknownCountry(countryRes.rows);

        this.timelineRows = {
          country: countryRows,
          asn: asnRes.rows || []
        };
        this.countryTimelineNote = `净变化 Top 12 · ${this.formatNumber(countryRows.length)} 个候选 · 按事件等距排列`;
        this.asnTimelineNote = `净变化 Top 12 · ${this.formatNumber(asnRes.total)} 个候选 · 按事件等距排列`;
        this.renderTimeline();
      } catch (error) {
        console.error("[ResolverEvolution] loadTimelines 失败:", error);
        this.countryTimelineNote = "加载失败";
        this.asnTimelineNote = "加载失败";
        this.showChartEmpty("timeline", "timelineContainer", "数据加载失败");
      } finally {
        this.timelineLoading = false;
      }
    },

    switchTimelineTab(tab) {
      if (this.timelineTab === tab) {
        return;
      }
      this.timelineTab = tab;
      this.renderTimeline();
    },

    // 异动时间线：横轴使用“事件位”而非连续时间轴，避免无事件时段占据空间、同日事件挤在一起
    renderTimeline() {
      const kind = this.timelineTab;
      const rows = (this.timelineRows[kind] || [])
        .slice()
        .sort((a, b) => Number(b.rank_score || b.severity || 0) - Number(a.rank_score || a.severity || 0))
        .slice(0, 12)
        .sort((a, b) => String(a.scan || "").localeCompare(String(b.scan || "")));

      if (!rows.length) {
        this.showChartEmpty("timeline", "timelineContainer", "暂无异动事件");
        return;
      }
      const maxSeverity = Math.max(1, ...rows.map(row => Math.abs(Number(row.severity || row.delta_pct || 0))));
      // 同一天可能有多个事件，加入索引生成唯一类目，使每个事件获得相同宽度的展示槽位
      const slots = rows.map((row, index) => `${row.scan}__${index}`);
      const points = rows.map((row, index) => {
        const label = kind === "asn" ? `AS${row.asn}` : row.country;
        const delta = Number(row.delta || 0);

        return {
          name: label,
          value: [slots[index], delta],
          symbolSize: Math.max(9, Math.min(24, 9 + Math.sqrt(Math.abs(Number(row.severity || 0)) / maxSeverity) * 15)),
          itemStyle: {
            color: delta >= 0 ? RISK_RED : DATA_BLUE,
            borderColor: "rgba(7,17,31,0.9)",
            borderWidth: 2
          },
          label: { position: delta >= 0 ? "top" : "bottom" },
          raw: row
        };
      });

      this.setChartOption("timeline", "timelineContainer", {
        tooltip: {
          trigger: "item",
          confine: true,
          formatter: (params) => {
            const row = params.data.raw || {};
            const title = kind === "asn"
              ? `AS${row.asn} ${this.shortText(row.org, 28)}`
              : row.country;

            return `${row.scan}<br/><b>${title}</b><br/>${row.display_note || row.note || ""}`;
          }
        },
        grid: { left: 8, right: 18, top: 34, bottom: 12, containLabel: true },
        xAxis: {
          type: "category",
          data: slots,
          boundaryGap: true,
          axisLabel: {
            interval: 0,
            hideOverlap: true,
            color: AXIS_TEXT,
            fontSize: 9,
            formatter: value => String(value).split("__")[0].slice(5)
          },
          axisLine: { lineStyle: { color: AXIS_LINE } },
          axisTick: { alignWithLabel: true, lineStyle: { color: AXIS_LINE } },
          splitLine: { show: false }
        },
        yAxis: {
          type: "value",
          name: "净变化",
          nameTextStyle: { color: AXIS_TEXT, fontSize: 10 },
          axisLabel: { color: AXIS_TEXT, fontSize: 10, formatter: value => this.formatCompact(value) },
          splitLine: { lineStyle: { color: SPLIT_LINE } }
        },
        series: [
          {
            type: "scatter",
            data: points,
            label: {
              show: true,
              distance: 5,
              color: "rgba(255,255,255,0.82)",
              fontSize: 9,
              formatter: params => this.shortText(params.data.name, 10)
            },
            labelLayout: { hideOverlap: true },
            markLine: {
              silent: true,
              symbol: "none",
              lineStyle: { color: "rgba(255,255,255,0.25)", type: "dashed" },
              label: { show: false },
              data: [{ yAxis: 0 }]
            }
          }
        ]
      });
    },

    // ==================== 异动事件查询 ====================
    async loadAnomalyEvents() {
      this.eventsLoading = true;
      try {
        const res = await getResolverAnomalyEvents();
        const rows = this.withoutUnknownCountry(res.rows).map(row => this.toEventRow(row));

        // 冻结以跳过 Vue 的深度响应式转换（约 1 万条）
        this.rawEventRows = Object.freeze(rows);
        this.yearOptions = Array.from(new Set(rows.map(row => String(row.scan || "").slice(0, 4))))
          .filter(Boolean)
          .sort((a, b) => b.localeCompare(a));
        this.applyEventFilter();
      } catch (error) {
        console.error("[ResolverEvolution] loadAnomalyEvents 失败:", error);
        this.rawEventRows = [];
        this.eventPageRows = [];
        this.eventTotal = 0;
        this.eventNote = "数据加载失败";
      } finally {
        this.eventsLoading = false;
      }
    },

    toEventRow(row) {
      const delta = Number(row.delta !== undefined ? row.delta : row.delta_pct) || 0;
      const isAsn = row.kind === "asn";

      return {
        scan: row.scan,
        kind: row.kind,
        country: row.country,
        asn: row.asn,
        org: row.org,
        direction: row.direction,
        typeLabel: isAsn ? "AS级" : "国家级",
        entity: isAsn ? `AS${row.asn} ${row.org || ""}`.trim() : (row.country || "--"),
        changeText: row.display_change || this.formatPct(row.delta_pct),
        note: row.display_note || row.note || "",
        toneClass: delta >= 0 ? "up" : "down",
        searchText: `${row.kind} ${row.country || ""} ${row.asn || ""} ${row.org || ""} ${row.note || ""} ${row.display_note || ""}`.toLowerCase()
      };
    },

    // 输入形如 US / CN_HK 视为国家，形如 4538 / AS4538 视为 AS，其余按全文匹配
    detectQuery(raw) {
      const text = String(raw || "").trim();

      if (!text) {
        return { mode: "none", key: "", text: "" };
      }
      const asnMatch = text.match(/^(?:AS)?\s*(\d+)$/i);

      if (asnMatch) {
        return { mode: "asn", key: asnMatch[1], text: text.toLowerCase() };
      }
      if (/^[a-z]{2}(?:_[a-z0-9]+)?$/i.test(text)) {
        return { mode: "country", key: this.normalizeCountryKey(text), text: text.toLowerCase() };
      }
      return { mode: "text", key: "", text: text.toLowerCase() };
    },

    normalizeCountryKey(value) {
      const text = String(value || "").trim().toUpperCase();

      if (text === "TW") {
        return "CN_TW";
      }
      if (text === "HK") {
        return "CN_HK";
      }
      return text;
    },

    onEventFilterChange() {
      if (this.eventFilterTimer) {
        clearTimeout(this.eventFilterTimer);
      }
      this.eventFilterTimer = setTimeout(() => this.applyEventFilter(), 250);
    },

    resetEventFilter() {
      this.eventSearch = "";
      this.eventDirection = "";
      this.eventYear = "";
      this.applyEventFilter();
    },

    applyEventFilter() {
      const query = this.detectQuery(this.eventSearch);
      const dir = this.eventDirection;
      const year = this.eventYear;
      const filtered = (this.rawEventRows || []).filter(row => {
        if (dir && row.direction !== dir) {
          return false;
        }
        if (year && !String(row.scan || "").startsWith(year)) {
          return false;
        }
        if (query.mode === "country") {
          return row.kind === "country" && String(row.country || "").toUpperCase() === query.key;
        }
        if (query.mode === "asn") {
          return row.kind === "asn" && String(row.asn || "") === query.key;
        }
        if (query.mode === "text") {
          return row.searchText.includes(query.text);
        }
        return true;
      });

      this.eventFilteredRows = filtered;
      this.eventTotal = filtered.length;
      this.eventPage = 1;
      this.updateEventPage();

      if (query.mode === "none") {
        const countryTotal = (this.rawEventRows || []).filter(row => row.kind === "country").length;
        const asnTotal = (this.rawEventRows || []).filter(row => row.kind === "asn").length;

        this.eventNote = `共 ${this.formatNumber(filtered.length)} 个异动事件 · 国家 ${this.formatNumber(countryTotal)} / ASN ${this.formatNumber(asnTotal)}`;
      } else if (query.mode === "country") {
        this.eventNote = `${query.key} 国家异动事件 ${this.formatNumber(filtered.length)} 条`;
      } else if (query.mode === "asn") {
        this.eventNote = `AS${query.key} 异动事件 ${this.formatNumber(filtered.length)} 条`;
      } else {
        this.eventNote = `全文筛选命中 ${this.formatNumber(filtered.length)} / ${this.formatNumber((this.rawEventRows || []).length)} 个异动事件`;
      }

      this.renderQueryProfile(query);
    },

    updateEventPage() {
      const start = (this.eventPage - 1) * this.eventPageSize;

      this.eventPageRows = (this.eventFilteredRows || []).slice(start, start + this.eventPageSize);
    },

    handleEventPageChange(page) {
      this.eventPage = page;
      this.updateEventPage();
    },

    // ==================== 查询画像（国家 / AS） ====================
    async fetchProfileSet(mode) {
      if (mode === "country") {
        if (!this.countryProfiles) {
          const res = await getResolverCountryProfiles();

          this.countryProfiles = Object.freeze(res.entities || {});
        }
        return this.countryProfiles;
      }
      if (mode === "asn") {
        if (!this.asnProfiles) {
          const res = await getResolverAsnProfiles();

          this.asnProfiles = Object.freeze(res.entities || {});
        }
        return this.asnProfiles;
      }
      return {};
    },

    async renderQueryProfile(query) {
      const token = ++this.queryToken;

      if (query.mode !== "country" && query.mode !== "asn") {
        this.profileVisible = false;
        return;
      }

      this.profileVisible = true;
      this.profileLoading = true;
      this.profileNote = "加载中";
      this.trajectoryItems = [];
      this.changeNotes = [];
      this.breakdownRows = [];
      // 画像区块默认隐藏，容器无尺寸，首次展示后再初始化图表实例
      await this.$nextTick();
      if (!this.profileChartInited && this.$refs.profileContainer) {
        this.charts.profile = echarts.init(this.$refs.profileContainer);
        this.profileChartInited = true;
      }

      try {
        const profiles = await this.fetchProfileSet(query.mode);

        if (token !== this.queryToken) {
          return;
        }
        const profile = profiles[query.key];

        if (!profile) {
          const label = query.mode === "country" ? query.key : `AS${query.key}`;

          this.profileTitle = `${label} 查询画像`;
          this.profileNote = "无画像数据";
          this.breakdownTitle = "Top 10 组成分布";
          this.breakdownNote = "--";
          this.showChartEmpty("profile", "profileContainer", "没有找到该对象的历史趋势");
          return;
        }

        const label = query.mode === "country"
          ? profile.country
          : `AS${profile.asn}${profile.org ? ` · ${this.shortText(profile.org, 30)}` : ""}`;

        this.profileTitle = profile.title || `${label} 历史解析器数量趋势`;
        this.profileNote = `最新 ${this.formatNumber(profile.latest_total)} 个`;
        this.setChartOption("profile", "profileContainer", this.buildLineOption([{
          name: query.mode === "country" ? profile.country : `AS${profile.asn}`,
          color: query.mode === "country" ? "#1890FF" : "#FAAD14",
          values: profile.values || []
        }], { area: true }));
        this.renderTrajectory(profile);

        if (query.mode === "country") {
          this.breakdownTitle = `${profile.country} 下 Top 10 AS 分布`;
          this.breakdownNote = "按最新轮解析器数量排序";
          this.breakdownRows = this.toBarRows(
            profile.top_asns || [],
            row => `AS${row.asn} ${this.shortText(row.org, 20)}`,
            "count",
            "tone-amber"
          );
        } else {
          this.breakdownTitle = `AS${profile.asn} 下 Top 10 /24 网络`;
          this.breakdownNote = profile.country ? `${profile.country} · 按最新轮数量排序` : "按最新轮解析器数量排序";
          this.breakdownRows = this.toBarRows(profile.top_net24 || [], row => row.net24, "count", "tone-blue");
        }
      } catch (error) {
        console.error("[ResolverEvolution] renderQueryProfile 失败:", error);
        if (token === this.queryToken) {
          this.profileNote = "画像加载失败";
          this.showChartEmpty("profile", "profileContainer", "画像加载失败");
        }
      } finally {
        if (token === this.queryToken) {
          this.profileLoading = false;
        }
      }
    },

    renderTrajectory(profile) {
      const traj = profile.trajectory || {};

      this.trajectoryItems = [
        { name: "首轮", value: this.formatNumber(traj.first_value), meta: traj.first_label || "--" },
        { name: "峰值", value: this.formatNumber(traj.peak_value), meta: traj.peak_label || "--" },
        { name: "最新", value: this.formatNumber(traj.latest_value), meta: traj.latest_label || "--" }
      ];

      const firstDelta = Number(traj.first_to_latest_delta || 0);
      const peakDelta = Number(traj.peak_to_latest_delta || 0);

      this.changeNotes = [
        {
          label: firstDelta >= 0 ? "较首轮激增" : "较首轮下降",
          tone: firstDelta >= 0 ? "rise" : "fall",
          text: `${this.formatSignedInt(firstDelta)} / ${this.formatTrajectoryPct(traj.first_to_latest_pct)}`
        },
        {
          label: peakDelta >= 0 ? "较峰值持平/增加" : "较峰值下降",
          tone: peakDelta >= 0 ? "rise" : "fall",
          text: `${this.formatSignedInt(peakDelta)} / ${this.formatTrajectoryPct(traj.peak_to_latest_pct)}`
        }
      ];
    },

    // ==================== 风险态势加载 ====================
    loadRiskPanels() {
      if (this.riskLoaded || this.riskLoading) {
        return this.riskLoading;
      }
      this.singleMapLoading = true;
      this.impactLoading = true;
      this.egressLoading = true;
      this.jurisdictionLoading = true;
      this.softwareLoading = true;

      this.riskLoading = Promise.all([
        this.loadSingleFailureMap(),
        this.loadImpactDistribution(),
        this.loadEgressMap(),
        this.loadJurisdiction(),
        this.loadSoftwareDatasets()
      ]).then(() => {
        this.riskLoaded = true;
        // 风险查询明细约 21MB / 12 万行，放在其他面板渲染完成后再拉取
        this.loadRiskQueryResults();
      }).catch(error => {
        console.error("[ResolverEvolution] loadRiskPanels 失败:", error);
      });

      return this.riskLoading;
    },

    async loadSingleFailureMap() {
      try {
        const data = await getResolverRiskSingleFailureMap();
        const nodes = data.nodes || [];
        const links = data.links || [];
        const nodeById = new Map(nodes.map(node => [node.id, node]));
        const maxWeight = Math.max(1, ...links.map(link => Number(link.weight || 0)));
        const upstream = nodes.filter(node => node.type === "upstream");
        const downstream = nodes.filter(node => node.type === "downstream");
        const lineData = links.map(link => {
          const source = nodeById.get(link.source);
          const target = nodeById.get(link.target);

          if (!source || !target) {
            return null;
          }
          return {
            coords: [[toLon(source.x), toLat(source.y)], [toLon(target.x), toLat(target.y)]],
            lineStyle: { width: 0.6 + Math.sqrt(Number(link.weight || 1) / maxWeight) * 2.6 },
            info: `${source.net24} → ${target.net24} · ${this.formatNumber(link.weight)}`
          };
        }).filter(Boolean);

        this.setChartOption("singleMap", "singleMapContainer", {
          tooltip: {
            trigger: "item",
            confine: true,
            formatter: params => {
              if (params.seriesType === "lines") {
                return params.data.info;
              }
              const node = params.data.raw || {};

              return `<b>${node.net24}</b><br/>${node.country || "--"} · ${this.shortText(node.org, 30)}<br/>`
                + `${node.type === "upstream" ? "被依赖解析器" : "依赖解析器"}: ${this.formatNumber(node.value)}`;
            }
          },
          geo: this.buildGeoOption(),
          series: [
            {
              name: "依赖边",
              type: "lines",
              coordinateSystem: "geo",
              zlevel: 1,
              polyline: false,
              lineStyle: { color: "rgba(230,103,103,0.55)", curveness: 0.22, opacity: 0.6 },
              data: lineData
            },
            {
              name: "下游 /24",
              type: "scatter",
              coordinateSystem: "geo",
              zlevel: 2,
              symbolSize: node => Math.max(3, Math.min(9, Math.sqrt(Number(node[2] || 1)) * 0.9)),
              itemStyle: { color: "rgba(64,169,255,0.7)" },
              data: downstream.map(node => ({
                value: [toLon(node.x), toLat(node.y), Number(node.value || 0)],
                raw: node
              }))
            },
            {
              name: "上游 /24",
              type: "effectScatter",
              coordinateSystem: "geo",
              zlevel: 3,
              rippleEffect: { brushType: "stroke", scale: 2.6 },
              symbolSize: node => Math.max(10, Math.min(24, Number(node[2] || 10) * 1.1)),
              itemStyle: { color: RISK_RED, shadowBlur: 10, shadowColor: "rgba(230,103,103,0.72)" },
              label: {
                show: true,
                position: "right",
                distance: 6,
                color: "#EDF5FF",
                fontSize: 10,
                fontWeight: "bold",
                formatter: params => params.data.raw.net24
              },
              data: upstream.map(node => ({
                value: [toLon(node.x), toLat(node.y), Number(node.radius || 10)],
                raw: node
              }))
            }
          ]
        });

        const summary = data.summary || {};

        this.singleMapNote = `上游 ${this.formatNumber(summary.selected_upstream_net24 || upstream.length)} 个 · 依赖边 ${this.formatNumber(links.length)} 条`;
      } catch (error) {
        console.error("[ResolverEvolution] loadSingleFailureMap 失败:", error);
        this.singleMapNote = "加载失败";
        this.showChartEmpty("singleMap", "singleMapContainer", "数据加载失败");
      } finally {
        this.singleMapLoading = false;
      }
    },

    async loadEgressMap() {
      try {
        const data = await getResolverRiskChinaEgressMap();
        const origin = data.origin || {};
        const countries = data.countries || [];
        const maxCount = Math.max(1, ...countries.map(row => Number(row.count || 0)));
        const originCoord = [toLon(origin.x), toLat(origin.y)];

        this.setChartOption("egressMap", "egressMapContainer", {
          tooltip: {
            trigger: "item",
            confine: true,
            formatter: params => {
              if (params.seriesType === "lines") {
                return params.data.info;
              }
              const row = params.data.raw || {};

              if (params.seriesName === "出境源") {
                return `中国解析器链路 ${this.formatNumber(row.count)} 条`;
              }
              return `<b>${row.country}</b> ${row.country_name || ""}<br/>`
                + `出境链路 ${this.formatNumber(row.count)} 条 · ${this.formatShare(row.share_pct)}`;
            }
          },
          geo: this.buildGeoOption(),
          series: [
            {
              name: "出境链路",
              type: "lines",
              coordinateSystem: "geo",
              zlevel: 1,
              effect: { show: true, period: 5, trailLength: 0.2, symbol: "arrow", symbolSize: 5, color: "rgba(255,255,255,0.8)" },
              lineStyle: { color: DATA_BLUE, curveness: 0.28, opacity: 0.58 },
              data: countries.map(row => ({
                coords: [originCoord, [toLon(row.x), toLat(row.y)]],
                lineStyle: { width: 0.8 + Math.sqrt(Number(row.count || 0) / maxCount) * 3.4 },
                info: `CN → ${row.country} · ${this.formatNumber(row.count)} · ${this.formatShare(row.share_pct)}`
              }))
            },
            {
              name: "出境目的地",
              type: "scatter",
              coordinateSystem: "geo",
              zlevel: 2,
              symbolSize: value => Math.max(6, Math.min(20, 5 + Math.sqrt(Number(value[2] || 0) / maxCount) * 15)),
              itemStyle: { color: DATA_BLUE },
              label: {
                show: true,
                position: "right",
                distance: 5,
                color: "#DBE9FF",
                fontSize: 10,
                formatter: params => `${params.data.raw.country} ${this.formatShare(params.data.raw.share_pct)}`
              },
              data: countries.slice(0, 10).map(row => ({
                value: [toLon(row.x), toLat(row.y), Number(row.count || 0)],
                raw: row
              })).concat(countries.slice(10).map(row => ({
                value: [toLon(row.x), toLat(row.y), Number(row.count || 0)],
                raw: row,
                label: { show: false }
              })))
            },
            {
              name: "出境源",
              type: "effectScatter",
              coordinateSystem: "geo",
              zlevel: 3,
              rippleEffect: { brushType: "stroke", scale: 3 },
              symbolSize: 16,
              itemStyle: { color: RISK_AMBER },
              label: {
                show: true,
                position: "top",
                color: "#FFE3AD",
                fontSize: 11,
                fontWeight: "bold",
                formatter: "CN 出境源"
              },
              data: [{ value: [originCoord[0], originCoord[1]], raw: origin }]
            }
          ]
        });

        const scope = data.scope || {};

        this.egressNote = `出境 ${this.formatNumber(scope.cross_country_edges)} 条 · ${this.formatShare(scope.cross_country_share_pct)}`;
      } catch (error) {
        console.error("[ResolverEvolution] loadEgressMap 失败:", error);
        this.egressNote = "加载失败";
        this.showChartEmpty("egressMap", "egressMapContainer", "数据加载失败");
      } finally {
        this.egressLoading = false;
      }
    },

    buildGeoOption() {
      return {
        map: "world",
        roam: true,
        zoom: 1.15,
        silent: true,
        itemStyle: {
          areaColor: "rgba(30,58,138,0.55)",
          borderColor: "rgba(77,166,255,0.35)",
          borderWidth: 0.6
        }
      };
    },

    async loadImpactDistribution() {
      try {
        const data = await getResolverRiskImpactDistribution();
        const rows = data.rows || [];
        const labels = rows.map(row => row.label).reverse();
        const providers = rows.map(row => Number(row.provider_count || 0)).reverse();
        const targets = rows.map(row => Number(row.unique_target_sum || 0)).reverse();

        // 两个指标量级不同，拆成并排的小倍图，各自使用独立基线，避免双轴叠加造成误读
        this.setChartOption("impact", "impactContainer", {
          title: [
            {
              text: "上游节点数",
              left: "24%",
              top: 0,
              textAlign: "center",
              textStyle: { color: "rgba(255,255,255,0.75)", fontSize: 11, fontWeight: "normal" }
            },
            {
              text: "影响解析器规模",
              left: "75%",
              top: 0,
              textAlign: "center",
              textStyle: { color: "rgba(255,255,255,0.75)", fontSize: 11, fontWeight: "normal" }
            }
          ],
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: params => {
              const item = params[0];

              return item ? `影响区间 ${item.axisValue}<br/>${item.marker}${item.seriesName}: ${this.formatNumber(item.value)}` : "";
            }
          },
          grid: [
            { left: 8, right: "53%", top: 28, bottom: 4, containLabel: true },
            { left: "53%", right: 18, top: 28, bottom: 4, containLabel: true }
          ],
          xAxis: [
            {
              type: "value",
              gridIndex: 0,
              axisLabel: { color: AXIS_TEXT, fontSize: 9, formatter: value => this.formatCompact(value) },
              splitLine: { lineStyle: { color: SPLIT_LINE } }
            },
            {
              type: "value",
              gridIndex: 1,
              axisLabel: { color: AXIS_TEXT, fontSize: 9, formatter: value => this.formatCompact(value) },
              splitLine: { lineStyle: { color: SPLIT_LINE } }
            }
          ],
          yAxis: [
            {
              type: "category",
              gridIndex: 0,
              data: labels,
              axisLabel: { color: AXIS_TEXT, fontSize: 10 },
              axisLine: { lineStyle: { color: AXIS_LINE } },
              axisTick: { show: false }
            },
            {
              type: "category",
              gridIndex: 1,
              data: labels,
              axisLabel: { show: false },
              axisLine: { show: false },
              axisTick: { show: false }
            }
          ],
          series: [
            {
              name: "上游节点数",
              type: "bar",
              xAxisIndex: 0,
              yAxisIndex: 0,
              barMaxWidth: 12,
              itemStyle: { color: DATA_BLUE_DARK, borderRadius: [0, 4, 4, 0] },
              data: providers
            },
            {
              name: "影响解析器规模",
              type: "bar",
              xAxisIndex: 1,
              yAxisIndex: 1,
              barMaxWidth: 12,
              itemStyle: { color: DATA_BLUE, borderRadius: [0, 4, 4, 0] },
              data: targets
            }
          ]
        });

        const scope = data.scope || {};

        this.impactNote = `单上游节点 ${this.formatNumber(scope.single_upstream_providers)} 个`;
      } catch (error) {
        console.error("[ResolverEvolution] loadImpactDistribution 失败:", error);
        this.impactNote = "加载失败";
        this.showChartEmpty("impact", "impactContainer", "数据加载失败");
      } finally {
        this.impactLoading = false;
      }
    },

    async loadJurisdiction() {
      try {
        const data = await getResolverRiskJurisdiction();
        const rows = data.rows || [];
        const scope = data.scope || {};

        // 管辖类型是离散排名，横向条形图比甜甜圈更省空间，也无需多套类别颜色
        this.setChartOption("jurisdiction", "jurisdictionContainer", this.buildHorizontalBarOption(
          rows,
          row => row.name,
          {
            valueKey: "share_pct",
            valueFormatter: value => `${Number(value || 0).toFixed(1)}%`
          }
        ));
        this.jurisdictionNote = `中国解析器链路 ${this.formatNumber(scope.china_forwarder_edges)} 条`;
      } catch (error) {
        console.error("[ResolverEvolution] loadJurisdiction 失败:", error);
        this.jurisdictionNote = "加载失败";
        this.showChartEmpty("jurisdiction", "jurisdictionContainer", "数据加载失败");
      } finally {
        this.jurisdictionLoading = false;
      }
    },

    buildDonutOption(rows, centerLabel, centerSub) {
      const list = rows || [];

      if (!list.length) {
        return {
          title: {
            text: "暂无数据",
            left: "center",
            top: "middle",
            textStyle: { color: "rgba(255,255,255,0.45)", fontSize: 13, fontWeight: "normal" }
          }
        };
      }

      return {
        color: list.map((_, index) => PALETTE[index] || DATA_SLATE),
        tooltip: {
          trigger: "item",
          confine: true,
          formatter: params => `${params.name}<br/>${this.formatNumber(params.value)} · ${this.formatShare(params.data.share_pct !== undefined ? params.data.share_pct : params.percent)}`
        },
        legend: {
          type: "scroll",
          orient: "vertical",
          right: 4,
          top: "center",
          textStyle: { color: "rgba(255,255,255,0.75)", fontSize: 11 },
          itemWidth: 10,
          itemHeight: 10,
          formatter: name => this.shortText(name, 14)
        },
        // 甜甜圈圆心文案：title 的 textAlign 会以 left 坐标为中心对齐，正好落在饼图圆心
        title: {
          text: centerLabel,
          subtext: centerSub,
          left: "34%",
          top: "44%",
          textAlign: "center",
          textStyle: { color: "#EDF5FF", fontSize: 20, fontWeight: "bold" },
          subtextStyle: { color: "rgba(255,255,255,0.6)", fontSize: 11 }
        },
        series: [
          {
            type: "pie",
            radius: ["42%", "66%"],
            center: ["34%", "52%"],
            avoidLabelOverlap: true,
            itemStyle: { borderColor: "rgba(0,0,0,0.35)", borderWidth: 2 },
            label: { show: false },
            labelLine: { show: false },
            data: list
          }
        ]
      };
    },

    // 6 个软件/CVE 面板共用同一份带 views 的数据，切换过滤器时只重渲染不重新请求
    async loadSoftwareDatasets() {
      try {
        const [type, impact, cveTop, matrix, level, legacy] = await Promise.all([
          getResolverRiskSoftwareType(),
          getResolverRiskSoftwareImpact(),
          getResolverRiskCveTop(),
          getResolverRiskSoftwareCveMatrix(),
          getResolverRiskLevelDistribution(),
          getResolverRiskLegacyVersionShare()
        ]);

        this.riskSoftwareData = Object.freeze({ type, impact, cveTop, matrix, level, legacy });
        const filters = (type && type.filters) || {};

        this.riskFilterSource = Object.freeze({
          countries: filters.countries || [],
          asns: filters.asns || [],
          orgs: filters.orgs || []
        });
        this.renderSoftwareCharts();
      } catch (error) {
        console.error("[ResolverEvolution] loadSoftwareDatasets 失败:", error);
        ["softwareType", "softwareImpact", "cveTop", "matrix", "level", "legacy"].forEach(key => {
          this.showChartEmpty(key, `${key}Container`, "数据加载失败");
        });
      } finally {
        this.softwareLoading = false;
      }
    },

    riskViewKey() {
      return this.riskFilterType && this.riskFilterValue
        ? `${this.riskFilterType}:${this.riskFilterValue}`
        : "global";
    },

    riskView(dataset) {
      if (!dataset || !dataset.views) {
        return {};
      }
      return dataset.views[this.riskViewKey()] || dataset.views.global || {};
    },

    onRiskFilterTypeChange() {
      this.riskFilterValue = "";
      this.renderSoftwareCharts();
    },

    resetRiskFilter() {
      this.riskFilterType = "";
      this.riskFilterValue = "";
      this.renderSoftwareCharts();
    },

    renderSoftwareCharts() {
      if (!this.riskSoftwareData || !this.riskSoftwareData.type) {
        return;
      }
      this.riskScopeLabel = this.buildRiskScopeLabel();
      this.renderSoftwareTypeChart();
      this.renderSoftwareImpactChart();
      this.renderCveTopChart();
      this.renderMatrixChart();
      this.renderLevelChart();
      this.renderLegacyChart();
    },

    buildRiskScopeLabel() {
      if (!this.riskFilterType || !this.riskFilterValue) {
        return "全部软件/CVE样本";
      }
      const typeLabel = this.riskFilterType === "country"
        ? "国家/地区"
        : this.riskFilterType === "asn" ? "AS" : "机构";
      const option = this.riskFilterValueOptions.find(item => item.key === this.riskFilterValue);

      return `${typeLabel} · ${option ? option.label : this.riskFilterValue}`;
    },

    renderSoftwareTypeChart() {
      const view = this.riskView(this.riskSoftwareData.type);

      this.setChartOption("softwareType", "softwareTypeContainer", this.buildDonutOption(
        (view.rows || []).map(row => ({
          name: row.name,
          value: Number(row.count || 0),
          share_pct: row.share_pct
        })),
        this.formatCompact(view.total || 0),
        "IP"
      ));
    },

    buildHorizontalBarOption(rows, labelFn, options = {}) {
      const list = rows || [];

      if (!list.length) {
        return {
          title: {
            text: "暂无数据",
            left: "center",
            top: "middle",
            textStyle: { color: "rgba(255,255,255,0.45)", fontSize: 13, fontWeight: "normal" }
          }
        };
      }
      const ordered = list.slice().reverse();
      const valueKey = options.valueKey || "count";
      const valueFormatter = options.valueFormatter || (value => this.formatCompact(value));

      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          confine: true,
          formatter: params => {
            const item = params[0];
            const raw = ordered[item.dataIndex] || {};
            const share = valueKey === "share_pct" ? "" : ` · ${this.formatShare(raw.share_pct)}`;

            return `${item.axisValue}<br/>${valueFormatter(item.value)}${share}`;
          }
        },
        grid: { left: 8, right: 44, top: 12, bottom: 4, containLabel: true },
        xAxis: {
          type: "value",
          axisLabel: { color: AXIS_TEXT, fontSize: 10, formatter: valueFormatter },
          splitLine: { lineStyle: { color: SPLIT_LINE } }
        },
        yAxis: {
          type: "category",
          data: ordered.map(labelFn),
          axisLabel: { color: AXIS_TEXT, fontSize: 11, formatter: value => this.shortText(value, 16) },
          axisLine: { lineStyle: { color: AXIS_LINE } },
          axisTick: { show: false }
        },
        series: [
          {
            type: "bar",
            barMaxWidth: 14,
            itemStyle: {
              borderRadius: [0, 4, 4, 0],
              color: options.color || DATA_BLUE
            },
            label: {
              show: true,
              position: "right",
              color: "rgba(255,255,255,0.8)",
              fontSize: 10,
              formatter: params => valueFormatter(params.value)
            },
            data: ordered.map(row => Number(row[valueKey] || 0))
          }
        ]
      };
    },

    renderSoftwareImpactChart() {
      const view = this.riskView(this.riskSoftwareData.impact);

      this.setChartOption("softwareImpact", "softwareImpactContainer", this.buildHorizontalBarOption(
        view.rows || [],
        row => row.name
      ));
    },

    renderCveTopChart() {
      const view = this.riskView(this.riskSoftwareData.cveTop);

      this.setChartOption("cveTop", "cveTopContainer", this.buildHorizontalBarOption(
        view.rows || [],
        row => String(row.cve || "").replace("CVE-", "")
      ));
    },

    renderMatrixChart() {
      const view = this.riskView(this.riskSoftwareData.matrix);
      const columns = view.columns || [];
      const rows = view.rows || [];

      if (!columns.length || !rows.length) {
        this.showChartEmpty("matrix", "matrixContainer", "暂无数据");
        return;
      }
      const cells = [];
      let max = 1;

      rows.forEach((row, rowIndex) => {
        (row.values || []).forEach((value, colIndex) => {
          const num = Number(value || 0);

          max = Math.max(max, num);
          cells.push([colIndex, rowIndex, num]);
        });
      });

      this.setChartOption("matrix", "matrixContainer", {
        tooltip: {
          position: "top",
          confine: true,
          formatter: params => {
            const row = rows[params.value[1]] || {};
            const share = (row.share_pct || [])[params.value[0]];

            return `${row.name} × CVE-${columns[params.value[0]]}<br/>`
              + `${this.formatNumber(params.value[2])}${share !== undefined ? ` · ${this.formatShare(share)}` : ""}`;
          }
        },
        grid: { left: 8, right: 12, top: 12, bottom: 40, containLabel: true },
        xAxis: {
          type: "category",
          data: columns,
          axisLabel: { color: AXIS_TEXT, fontSize: 10, rotate: 32 },
          axisLine: { lineStyle: { color: AXIS_LINE } },
          splitArea: { show: true, areaStyle: { color: ["rgba(255,255,255,0.02)", "transparent"] } }
        },
        yAxis: {
          type: "category",
          data: rows.map(row => row.name),
          axisLabel: { color: AXIS_TEXT, fontSize: 11 },
          axisLine: { lineStyle: { color: AXIS_LINE } }
        },
        visualMap: {
          min: 0,
          max,
          calculable: false,
          show: false,
          inRange: { color: ["rgba(57,135,229,0.16)", "rgba(57,135,229,0.52)", DATA_BLUE] }
        },
        series: [
          {
            type: "heatmap",
            data: cells,
            label: {
              show: true,
              color: "#fff",
              fontSize: 10,
              formatter: params => this.formatCompact(params.value[2])
            },
            itemStyle: { borderColor: "rgba(0,0,0,0.35)", borderWidth: 1, borderRadius: 3 }
          }
        ]
      });
    },

    renderLevelChart() {
      const view = this.riskView(this.riskSoftwareData.level);
      const rows = view.rows || [];

      if (!rows.length) {
        this.showChartEmpty("level", "levelContainer", "暂无数据");
        return;
      }
      // 风险等级是状态语义，固定使用状态色；其他图表保持单一蓝色数据色
      const statusColor = name => {
        const key = String(name || "").toLowerCase();

        if (key.includes("critical") || key.includes("严重")) {
          return RISK_RED;
        }
        if (key.includes("high") || key.includes("高危")) {
          return RISK_ORANGE;
        }
        if (key.includes("medium") || key.includes("中危")) {
          return RISK_AMBER;
        }
        if (key.includes("low") || key.includes("低危")) {
          return DATA_BLUE_LIGHT;
        }
        return DATA_SLATE;
      };
      const segmentNames = [];
      const segmentColors = {};

      rows.forEach(row => {
        (row.segments || []).forEach(seg => {
          if (!segmentNames.includes(seg.name)) {
            segmentNames.push(seg.name);
            segmentColors[seg.name] = statusColor(seg.name);
          }
        });
      });
      const ordered = rows.slice().reverse();

      this.setChartOption("level", "levelContainer", {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          confine: true,
          formatter: params => {
            const head = params[0] ? params[0].axisValue : "";

            return `${head}<br/>`
              + params.filter(item => Number(item.value) > 0)
                .map(item => `${item.marker}${item.seriesName}: ${this.formatNumber(item.value)}`)
                .join("<br/>");
          }
        },
        legend: {
          data: segmentNames,
          top: 2,
          textStyle: { color: "rgba(255,255,255,0.75)", fontSize: 10 },
          itemWidth: 12,
          itemHeight: 8
        },
        grid: { left: 8, right: 30, top: 32, bottom: 4, containLabel: true },
        xAxis: {
          type: "value",
          axisLabel: { color: AXIS_TEXT, fontSize: 10, formatter: value => this.formatCompact(value) },
          splitLine: { lineStyle: { color: SPLIT_LINE } }
        },
        yAxis: {
          type: "category",
          data: ordered.map(row => row.name),
          axisLabel: { color: AXIS_TEXT, fontSize: 11 },
          axisLine: { lineStyle: { color: AXIS_LINE } },
          axisTick: { show: false }
        },
        series: segmentNames.map(name => ({
          name,
          type: "bar",
          stack: "level",
          barMaxWidth: 18,
          itemStyle: {
            color: segmentColors[name] || DATA_SLATE,
            borderColor: "rgba(7,17,31,0.9)",
            borderWidth: 1
          },
          data: ordered.map(row => {
            const seg = (row.segments || []).find(item => item.name === name);

            return seg ? Number(seg.count || 0) : 0;
          })
        }))
      });
    },

    renderLegacyChart() {
      const view = this.riskView(this.riskSoftwareData.legacy);

      this.setChartOption("legacy", "legacyContainer", this.buildHorizontalBarOption(
        view.rows || [],
        row => row.name
      ));
      this.legacyVersions = (view.top_versions || []).slice(0, 5).map(row => ({
        version: row.version,
        shareText: this.formatShare(row.share_pct)
      }));
    },

    // ==================== 风险查询 ====================
    async loadRiskQueryResults() {
      this.riskQueryLoading = true;
      this.riskQueryNote = "正在加载风险检测明细（约 21MB）";
      try {
        const data = await getResolverRiskQueryResults();
        const columns = data.columns || [];

        this.riskQueryColumnIndex = {};
        columns.forEach((name, index) => {
          this.riskQueryColumnIndex[name] = index;
        });
        // 12 万行保持原始二维数组，仅在渲染当前页时转成对象，避免整表对象化
        this.rawRiskQueryRows = Object.freeze(data.rows || []);
        this.riskTypeOptions = ((data.filters || {}).risk_types) || [];
        this.applyRiskQueryFilter();
      } catch (error) {
        console.error("[ResolverEvolution] loadRiskQueryResults 失败:", error);
        this.rawRiskQueryRows = [];
        this.riskQueryPageRows = [];
        this.riskQueryTotal = 0;
        this.riskQueryNote = "风险查询数据加载失败";
      } finally {
        this.riskQueryLoading = false;
      }
    },

    onRiskQueryFilterChange() {
      if (this.riskQueryFilterTimer) {
        clearTimeout(this.riskQueryFilterTimer);
      }
      this.riskQueryFilterTimer = setTimeout(() => this.applyRiskQueryFilter(), 300);
    },

    resetRiskQueryFilter() {
      this.rqOrg = "";
      this.rqType = "";
      this.rqCountry = "";
      this.rqAsn = "";
      this.applyRiskQueryFilter();
    },

    applyRiskQueryFilter() {
      const idx = this.riskQueryColumnIndex;
      const org = this.rqOrg.trim().toLowerCase();
      const type = this.rqType;
      const country = this.rqCountry.trim().toLowerCase();
      const asn = this.rqAsn.trim().replace(/^as/i, "");
      const active = Boolean(org || type || country || asn);
      const asnIsNumeric = /^\d+$/.test(asn);
      const filtered = (this.rawRiskQueryRows || []).filter(row => {
        if (org && !String(row[idx.org] || "").toLowerCase().includes(org)) {
          return false;
        }
        if (country) {
          const haystack = `${row[idx.country] || ""} ${row[idx.country_name] || ""}`.toLowerCase();

          if (!haystack.includes(country)) {
            return false;
          }
        }
        if (asn) {
          const rowAsn = String(row[idx.asn] || "");

          if (asnIsNumeric ? rowAsn !== asn : !`as${rowAsn}`.toLowerCase().includes(asn.toLowerCase())) {
            return false;
          }
        }
        if (type) {
          if (type === "存在CVE") {
            return Number(row[idx.cve_count] || 0) > 0;
          }
          return String(row[idx.risk_types] || "").split("、").includes(type);
        }
        return true;
      });

      this.riskQueryFilteredRows = filtered;
      this.riskQueryTotal = filtered.length;
      this.riskQueryPage = 1;
      this.updateRiskQueryPage();
      this.riskQueryNote = active
        ? `筛选命中 ${this.formatNumber(filtered.length)} / ${this.formatNumber((this.rawRiskQueryRows || []).length)} 条`
        : `共 ${this.formatNumber(filtered.length)} 条风险检测结果`;
    },

    updateRiskQueryPage() {
      const idx = this.riskQueryColumnIndex;
      const start = (this.riskQueryPage - 1) * this.riskQueryPageSize;

      this.riskQueryPageRows = (this.riskQueryFilteredRows || [])
        .slice(start, start + this.riskQueryPageSize)
        .map(row => {
          const country = row[idx.country];
          const countryName = row[idx.country_name];

          return {
            detect_date: row[idx.detect_date],
            resolver: row[idx.resolver],
            role: row[idx.role],
            riskTags: String(row[idx.risk_types] || "未见明显结构风险").split("、").filter(Boolean),
            cveText: row[idx.cve_text] || "否",
            cveHit: Number(row[idx.cve_count] || 0) > 0,
            org: row[idx.org] || "-",
            countryLabel: countryName ? `${country} · ${countryName}` : (country || "-"),
            asnLabel: `AS${row[idx.asn]}`
          };
        });
    },

    handleRiskQueryPageChange(page) {
      this.riskQueryPage = page;
      this.updateRiskQueryPage();
    }
  }
};
</script>

<style lang="less" scoped>
.resolver-container {
  width: 100%;
  min-height: var(--screen-height, 100vh);
  height: var(--screen-height, 100vh);
  background: url("../assets/background.png") no-repeat center center #000;
  background-size: cover;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
}

.leftSider, .rightSider {
  position: fixed;
  z-index: 1;
  display: none;

  @media (min-width: 1366px) {
    display: block;
  }

  img {
    height: var(--screen-height, 100vh);
    width: auto;
  }
}

.leftSider { left: 0; top: 0; }
.rightSider { right: 0; top: 0; }

.page-update-time {
  position: absolute;
  top: 120px;
  left: 5%;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  z-index: 11;

  .dot { margin: 0 6px; }

  .highlight {
    color: #40a9ff;
    font-weight: bold;
  }
}

// 基本态势顶部的机构解析链检索入口，位置让开左上角观测周期与右上角态势切换
.org-entry-panel {
  position: absolute;
  top: 104px;
  left: 50%;
  transform: translateX(-50%);
  width: min(720px, calc(100% - 520px));
  min-width: 320px;
  z-index: 12;
  padding: 8px 12px 9px;
  border: 1px solid rgba(126, 214, 255, 0.36);
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(17, 32, 78, 0.92), rgba(25, 59, 111, 0.74));
  box-shadow: 0 10px 28px rgba(0, 18, 56, 0.26), inset 0 0 20px rgba(61, 173, 255, 0.08);
  box-sizing: border-box;

  // 窄屏放不下居中的横向检索条，退回顶部横幅式排布
  @media (max-width: 1200px) {
    top: 96px;
    left: 2%;
    right: 2%;
    width: auto;
    min-width: 0;
    transform: none;
  }

  .org-entry-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .org-entry-label {
    flex: 0 0 auto;
    color: #7ed6ff;
    font-size: 13px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }

  /deep/ .org-entry-input {
    flex: 1;
  }

  /deep/ .el-input__inner {
    background-color: rgba(7, 18, 49, 0.72);
    border: 1px solid rgba(126, 214, 255, 0.48);
    border-right: none;
    color: #fff;
    font-size: 13px;
    height: 32px;
    line-height: 32px;

    &::placeholder { color: rgba(255, 255, 255, 0.5); }
  }

  /deep/ .el-input-group__append {
    background: linear-gradient(135deg, rgba(21, 126, 251, 0.92), rgba(35, 198, 255, 0.84));
    border: 1px solid rgba(126, 214, 255, 0.55);
    border-left: none;
    color: #fff;
    padding: 0 12px;
  }

  /deep/ .el-input__suffix {
    right: 48px;
  }

  .org-entry-samples {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 7px;
  }

  .sample-label {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
  }

  .sample-chip {
    padding: 2px 9px;
    border: 1px solid rgba(126, 214, 255, 0.34);
    border-radius: 11px;
    background: rgba(21, 60, 116, 0.5);
    color: rgba(219, 233, 255, 0.88);
    font-size: 12px;
    line-height: 18px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: rgba(126, 214, 255, 0.85);
      background: rgba(35, 122, 210, 0.6);
      color: #fff;
    }
  }
}

// 机构解析链子页面顶部：返回总览
.org-back-bar {
  display: flex;
  align-items: center;
  gap: 12px;

  .org-back-note {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
  }
}

.range-switch {
  position: absolute;
  top: 110px;
  right: 5%;
  z-index: 11;
}

.main-content {
  position: absolute;
  top: 180px;
  left: 0;
  right: 0;
  bottom: 80px;
  padding: 0 2% 80px;
  z-index: 10;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  overflow-x: hidden;
  box-sizing: border-box;
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (min-width: 768px) {
    top: 200px;
    padding: 0 4% 80px;
    gap: 20px;
  }

  @media (min-width: 1366px) {
    padding: 0 5% 80px;
  }
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (min-width: 768px) {
    gap: 20px;
  }
}

.grid-row {
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;

  @media (min-width: 1280px) {
    display: grid;
    gap: 20px;
  }
}

.row-basic {
  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr);
  }
}

.risk-overview-grid {
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 20px;

    .risk-single-map-card,
    .risk-egress-map-card {
      grid-column: span 3;
    }

    .risk-impact-card {
      grid-column: span 4;
    }

    .risk-jurisdiction-card {
      grid-column: span 2;
    }
  }
}

.row-software {
  @media (min-width: 900px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.col-stack {
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;

  @media (min-width: 1280px) {
    gap: 20px;
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: clamp(12px, 1.6vw, 18px);
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.chart-card {
  flex: 1;
  min-height: 280px;
}

.map-card {
  min-height: 390px;

  // 地图由多个 canvas 图层组成，标题需要拥有独立层叠上下文，避免视觉上被地图盖住
  .card-title {
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    min-height: 24px;
    margin-bottom: 16px;
    overflow: visible;

    .title-main {
      flex-shrink: 0;
      white-space: nowrap;
    }
  }

  .map-chart {
    position: relative;
    z-index: 1;
  }
}

.compact-chart {
  min-height: 250px;
}

.stability-card {
  flex: 0 0 auto;
}

.card-title {
  font-size: clamp(13px, 1.6vw, 17px);
  color: #fff;
  font-weight: bold;
  margin-bottom: 12px;
  padding-left: 10px;
  border-left: 4px solid #1890FF;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .title-main {
    display: inline-flex;
    align-items: center;
    min-width: 0;
  }

  .subtitle {
    font-size: clamp(11px, 1.3vw, 13px);
    font-weight: normal;
    color: rgba(255, 255, 255, 0.6);
    margin-left: 8px;
  }
}

.chart-container {
  width: 100%;
  flex: 1;
  min-height: 230px;
}

.map-chart {
  min-height: 340px;
}

.legacy-chart {
  min-height: 190px;
}

.profile-chart {
  min-height: 200px;
  flex: none;
  height: 200px;
}

// 板块内的切换按钮
.tab-switch {
  margin-left: auto;
  display: inline-flex;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;

  button {
    border: none;
    outline: none;
    cursor: pointer;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: normal;
    background: transparent;
    color: rgba(255, 255, 255, 0.65);
    transition: background 0.2s, color 0.2s;

    & + button {
      border-left: 1px solid rgba(255, 255, 255, 0.18);
    }

    &:hover {
      color: #fff;
      background: rgba(24, 144, 255, 0.18);
    }

    &.active {
      background: #1890FF;
      color: #fff;
    }
  }
}

// ---------- KPI ----------
.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  min-height: 90px;
}

.metric-box {
  border: 1px solid rgba(92, 136, 216, 0.22);
  border-radius: 6px;
  padding: 10px;
  background: linear-gradient(135deg, rgba(39, 71, 122, 0.42), rgba(24, 35, 57, 0.72));

  .metric-value {
    font-size: clamp(18px, 1.6vw, 26px);
    line-height: 1.1;
    font-weight: bold;
    color: #EEF6FF;
    font-variant-numeric: tabular-nums;
  }

  .metric-label {
    margin-top: 6px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
  }
}

// ---------- 条形列表 ----------
.bar-list {
  display: grid;
  gap: 10px;
  min-height: 60px;
  align-content: start;
}

.bar-row {
  display: grid;
  grid-template-columns: minmax(70px, 1.1fr) minmax(60px, 1.8fr) minmax(72px, 0.8fr);
  align-items: center;
  gap: 10px;
  min-height: 30px;
}

.bar-name {
  color: rgba(255, 255, 255, 0.9);
  font-weight: bold;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar-track {
  height: 10px;
  border-radius: 999px;
  background: rgba(111, 139, 190, 0.15);
  overflow: hidden;
  border: 1px solid rgba(122, 154, 216, 0.14);
}

.bar-fill {
  height: 100%;
  min-width: 2px;
  border-radius: inherit;
  transition: width 0.6s ease;

  &.tone-blue { background: linear-gradient(90deg, #1890FF, #13C2C2); }
  &.tone-amber { background: linear-gradient(90deg, #FAAD14, #FF7D4A); }
  &.tone-stable { background: linear-gradient(90deg, #52C41A, #13C2C2); }
  &.tone-high { background: linear-gradient(90deg, #13C2C2, #1890FF); }
  &.tone-volatile { background: linear-gradient(90deg, #FAAD14, #FF7D4A); }
  &.tone-ephemeral { background: linear-gradient(90deg, #8290A8, #536176); }
}

.bar-count {
  color: rgba(255, 255, 255, 0.6);
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  line-height: 1.2;

  strong {
    display: block;
    color: #fff;
    font-size: 13px;
  }

  small {
    display: block;
    margin-top: 2px;
    color: #13C2C2;
    font-size: 11px;
    font-weight: bold;
  }
}

// ---------- 稳定分类 ----------
.stability-wrap {
  display: grid;
  gap: 9px;
  min-height: 60px;
  align-content: start;
}

.stability-row {
  display: grid;
  grid-template-columns: minmax(80px, 1fr) 1.1fr minmax(72px, auto);
  align-items: center;
  gap: 10px;
  padding: 8px 9px;
  border: 1px solid rgba(110, 145, 203, 0.18);
  border-radius: 6px;
  background: rgba(25, 38, 61, 0.5);
}

.stab-info { min-width: 0; }

.stab-name {
  color: #fff;
  font-weight: bold;
  font-size: 13px;
}

.stab-range {
  display: block;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 2px;
  font-size: 11px;
}

.stab-value {
  text-align: right;
  font-size: 15px;
  font-weight: bold;
  color: #fff;
  font-variant-numeric: tabular-nums;

  small {
    display: block;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 2px;
    font-size: 11px;
    font-weight: normal;
  }
}

// ---------- 查询工具栏 ----------
.query-toolbar {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-bottom: 12px;

  @media (min-width: 900px) {
    grid-template-columns: minmax(220px, 1.6fr) 150px 150px 90px;
  }
}

.risk-query-toolbar {
  @media (min-width: 900px) {
    grid-template-columns: minmax(180px, 1.2fr) minmax(160px, 1fr) 140px 130px 90px;
  }
}

// ---------- 查询画像 ----------
.query-insight {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin: 2px 0 14px;
  padding: 14px 0;
  border-top: 1px solid rgba(114, 149, 207, 0.16);
  border-bottom: 1px solid rgba(114, 149, 207, 0.16);

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  }
}

.query-cell { min-width: 0; }

.query-cell-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 14px;
    color: #fff;
  }

  span {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    white-space: nowrap;
  }
}

.trajectory-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.trajectory-item {
  padding: 8px 10px;
  border-left: 3px solid rgba(19, 194, 194, 0.7);
  background: rgba(20, 34, 55, 0.46);

  .traj-name {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
  }

  .traj-value {
    margin-top: 3px;
    color: #fff;
    font-size: 17px;
    font-weight: bold;
    font-variant-numeric: tabular-nums;
  }

  .traj-meta {
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
  }
}

.trajectory-note {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 9px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;

  .rise { color: #FF9A9A; }
  .fall { color: #69C0FF; }
}

// ---------- 风险过滤器 ----------
.filter-card {
  padding: 12px clamp(12px, 1.6vw, 18px);
}

.risk-filter {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 10px;

  @media (min-width: 900px) {
    grid-template-columns: auto 130px minmax(200px, 320px) 80px 1fr;
  }

  .filter-label {
    color: rgba(255, 255, 255, 0.75);
    font-size: 13px;
    white-space: nowrap;
  }

  .filter-scope {
    color: rgba(255, 255, 255, 0.6);
    font-size: 13px;

    @media (min-width: 900px) {
      text-align: right;
    }

    b { color: #fff; }
  }
}

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.risk-pill {
  border: 1px solid rgba(122, 154, 216, 0.2);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(31, 43, 67, 0.58);
  padding: 3px 9px;
  font-size: 11px;
}

// ---------- 表格内标签 ----------
.direction-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;

  &.up {
    color: #FF9A9A;
    background: rgba(255, 107, 107, 0.16);
  }

  &.down {
    color: #69C0FF;
    background: rgba(24, 144, 255, 0.16);
  }
}

.resolver-cell {
  color: #EEF6FF;
  font-weight: bold;
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 12px;
}

.risk-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.risk-tag {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(255, 183, 77, 0.3);
  border-radius: 999px;
  padding: 1px 7px;
  color: #FFE3AD;
  background: rgba(250, 173, 20, 0.13);
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;

  &.neutral {
    color: rgba(255, 255, 255, 0.6);
    border-color: rgba(122, 154, 216, 0.2);
    background: rgba(31, 43, 67, 0.58);
  }
}

.cve-cell {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;

  &.hit {
    color: #FFD1D6;
    font-weight: bold;
  }
}

.empty-tip {
  padding: 20px;
  color: rgba(255, 255, 255, 0.45);
  text-align: center;
  font-size: 13px;
}

// ---------- 表格深色主题 ----------
/deep/ .el-table {
  background: transparent;
  color: #fff;

  &::before { display: none; }

  th.el-table__cell {
    background: rgba(24, 144, 255, 0.1);
    color: #fff;
    font-weight: bold;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  tr {
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
  }

  td.el-table__cell {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    padding: 6px 0;
  }

  .cell {
    padding-left: 8px;
    padding-right: 8px;
  }

  tbody tr:hover > td.el-table__cell {
    background: rgba(24, 144, 255, 0.12) !important;
  }

  .el-table__body-wrapper::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  .el-table__body-wrapper::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
}

// 两张查询表每页固定 20 条，直接展开整页；仅保留窄屏时的横向滚动能力
.event-table, .risk-query-table {
  /deep/ .el-table__body-wrapper {
    height: auto !important;
    max-height: none !important;
    overflow-y: hidden;
  }
}

// ---------- 输入控件深色 ----------
// el-select 默认是 inline-block，放进 grid 单元格需要撑满
/deep/ .el-select {
  width: 100%;
}

/deep/ .el-input__inner {
  background: rgba(31, 43, 67, 0.78);
  border-color: rgba(126, 156, 210, 0.27);
  color: #fff;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }
}

/deep/ .el-button--small:not(.el-button--primary) {
  background: rgba(31, 43, 67, 0.78);
  border-color: rgba(126, 156, 210, 0.27);
  color: rgba(255, 255, 255, 0.75);

  &:hover {
    color: #fff;
    border-color: rgba(77, 151, 255, 0.58);
    background: rgba(24, 144, 255, 0.18);
  }
}

/deep/ .el-pagination {
  margin-top: 8px;
  text-align: center;

  button, .el-pager li {
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
  }

  .el-pager li.active { color: #40a9ff; }
  .el-pagination__total { color: rgba(255, 255, 255, 0.7); }
}

.beian-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 0 4px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 12px;

  a {
    color: rgba(255, 255, 255, 0.55);
    text-decoration: none;

    &:hover { color: #40a9ff; }
  }

  .beian-police-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .beian-police-logo {
    width: 14px;
    height: 14px;
  }
}
</style>
